import { useMemo } from 'react'
import { Globe, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { detectTimezone, knownTimezones, todayIn } from '@trackmind/core'
import type { UserPreferences } from '@trackmind/core'

/**
 * Which time zone your days belong to.
 *
 * This exists because the answer used to be implicit. The app runs on your
 * machine, so `new Date()` was already your local time and nothing had to
 * decide anything. Then the MCP server needed the same answer from a
 * datacentre in UTC, got it wrong by a day for every evening after 5pm, and
 * there was no setting to consult and nothing on screen to check.
 *
 * Detected automatically — it is a fact the browser already knows, not a
 * question worth asking at onboarding. Shown so it can be verified, and
 * overridable for the cases detection gets wrong: a work machine set to
 * head-office time, or a trip where you want your days anchored home.
 *
 * Deliberately NOT a fixed offset like "PDT". The difference between PDT and
 * PST is exactly what a fixed offset gets wrong, twice a year, silently. An
 * IANA zone carries its own daylight-saving rules.
 *
 * Two views: the same control on both, but the picker is full-width and 44px
 * on a phone with the reset button beneath it, because a picker and a button
 * side by side leave neither enough room — and a non-wrapping row wider than
 * the viewport is what makes iOS Safari zoom the whole page out.
 */

interface TimezoneSettingProps {
  preferences: UserPreferences
  onChange: (next: UserPreferences) => void
}

export function TimezoneSetting({ preferences, onChange }: TimezoneSettingProps) {
  const detected = detectTimezone()
  const current = preferences.timezone ?? detected ?? 'UTC'
  const isManual = preferences.timezone_source === 'manual'

  const zones = useMemo(() => {
    const all = knownTimezones()
    // Older engines have no `supportedValuesOf`. Offering the current and
    // detected zones is honest — better than a hardcoded list that happens to
    // omit the user's own.
    if (all.length === 0) {
      return [...new Set([current, detected, 'UTC'].filter(Boolean) as string[])]
    }
    return all
  }, [current, detected])

  const pick = (zone: string) => {
    onChange({ ...preferences, timezone: zone, timezone_source: 'manual' })
  }

  const reset = () => {
    onChange({ ...preferences, timezone: detected, timezone_source: 'auto' })
  }

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium" htmlFor="tz">
        <Globe className="h-4 w-4" />
        Time zone
      </label>
      <p className="text-sm text-muted-foreground">
        Which zone your days start and end in. Detected automatically &mdash; change it
        only if that is wrong. An assistant connected to TrackMind uses this to work out
        what &ldquo;today&rdquo; means.
      </p>
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <Select value={current} onValueChange={pick}>
          <SelectTrigger id="tz" className="h-11 md:h-10 md:w-[280px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {zones.map((zone) => (
              <SelectItem key={zone} value={zone}>{zone}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isManual && detected && detected !== current && (
          <Button variant="ghost" onClick={reset} className="h-11 md:h-10 justify-start">
            <RotateCcw className="mr-2 h-4 w-4" />
            Use this device ({detected})
          </Button>
        )}
      </div>
      {/* The line that makes it checkable at a glance. A wrong zone is
          otherwise invisible until something answers with the wrong day. */}
      <p className="text-xs text-muted-foreground">
        Today here is <span className="font-mono">{todayIn(current)}</span>
        {isManual ? ' · set by you' : ' · detected'}
      </p>
    </div>
  )
}
